import { Typography } from '@mui/material';
import styled from 'styled-components';
import { formatShortDateTime, localizeDate } from '../../utils/datetime';
import { pluralizeString } from '../../utils/common';
import Dialog from '../Dialog';
import { FlexCenter, FlexColumn } from '../Flex';
import {
  fontFamily,
  fontSize,
  fontWeight,
  radius,
  space,
} from '../../style/utils';

const SamplesList = styled(FlexColumn)`
  max-height: 200px;
  overflow-y: auto;
  background-color: ${({ theme }) => theme.palette.surface.strong};
  border-radius: ${radius('sm')};
  padding: ${space('sm')};
  gap: ${space('xs')};
`;

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm: (start: number, end: number) => void;
  startTime?: number;
  endTime?: number;
  excludedSamples?: Array<{ timestamp: number }>;
};

const ExcludeConfirmationDialog = ({
  open,
  onClose,
  onConfirm,
  startTime,
  endTime,
  excludedSamples = [],
}: Props) => {
  if (!startTime || !endTime) return null;

  const formatDisplayTime = (timestamp: number) =>
    formatShortDateTime(localizeDate(timestamp));

  const sampleCount = excludedSamples.length;

  return (
    <Dialog
      open={open}
      title="Do you want to exclude this time range from the training period?"
      message={
        <FlexColumn sx={{ gap: 2 }}>
          <FlexCenter sx={{ gap: 1 }}>
            <Typography>{formatDisplayTime(startTime)}</Typography>-
            <Typography>{formatDisplayTime(endTime)}</Typography>
          </FlexCenter>

          {sampleCount > 0 && (
            <FlexColumn sx={{ gap: 1 }}>
              <Typography sx={{ fontWeight: fontWeight('semibold') }}>
                {pluralizeString(sampleCount, 'sample', 'samples')} will be
                excluded:
              </Typography>
              <SamplesList>
                {excludedSamples.map((sample, index) => (
                  <Typography
                    key={`${sample.timestamp}-${index}`}
                    sx={{
                      fontSize: fontSize('sm'),
                      fontFamily: fontFamily('mono'),
                    }}
                  >
                    {formatDisplayTime(sample.timestamp)}
                  </Typography>
                ))}
              </SamplesList>
            </FlexColumn>
          )}
        </FlexColumn>
      }
      onClose={onClose}
      onConfirmButtonClick={() => onConfirm(startTime, endTime)}
      confirmText="Exclude Range"
    />
  );
};

export default ExcludeConfirmationDialog;
